if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = this.dataset.bsPurchaseBlocked === 'true';
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.dataset.bsPurchaseBlocked === 'true') {
          this.toggleSubmitButton(true, this.dataset.bsPurchaseBlockedMessage);
          this.handleErrorMessage(this.dataset.bsPurchaseBlockedMessage);
          return;
        }
        if (this.beststarCartNeedsRefresh || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (soldOutMessage) {
                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButtonText.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
                this.error = true;
              }
              return this.refreshBeststarCartAfterAddError(variantId, response.description || response.message);
            } else if (!this.cart || window.matchMedia('(max-width: 749px)').matches) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            const message = window.cartStrings.error;
            this.handleErrorMessage(message);
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: variantId,
              errors: message,
              message,
            });
            this.dispatchCartErrorEvent(message, 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
            // A failed response does not establish whether the add reached Shopify.
            return this.refreshBeststarCartAfterAddError(variantId, message, true);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (!this.error && !this.beststarCartNeedsRefresh) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      async refreshBeststarCartAfterAddError(variantId, message, requireCartReview = false) {
        try {
          const response = await fetch(`${routes.cart_url}.js`, {
            cache: 'no-store',
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) throw new Error('Cart read failed');
          const cart = await response.json();
          if (!Number.isInteger(cart.item_count) || cart.item_count < 0 || !Array.isArray(cart.items) ||
              cart.items.some((item) => !Number.isInteger(item.quantity) || item.quantity < 0) ||
              cart.items.reduce((count, item) => count + item.quantity, 0) !== cart.item_count) {
            throw new Error('Invalid cart read');
          }
          // Subscribers update the mobile badges and fetch native product quantity rules.
          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: variantId,
            cartData: cart,
          });
          const productInfo = this.closest('product-info');
          const quantityInput = productInfo?.querySelector('.quantity__input');
          if (quantityInput && !productInfo.dataset.originalSection) {
            const cartQuantity = cart.items.reduce((count, item) =>
              count + (String(item.variant_id || item.id) === String(variantId) ? item.quantity : 0), 0);
            if (parseInt(quantityInput.dataset.cartQuantity) !== cartQuantity) {
              throw new Error('Product quantity rules did not confirm the cart');
            }
          }
          if (requireCartReview) this.blockBeststarAddUntilRefresh(message, true);
        } catch (error) {
          console.error(error);
          this.blockBeststarAddUntilRefresh(message, false);
        }
      }

      blockBeststarAddUntilRefresh(message, reviewOnly) {
        this.beststarCartNeedsRefresh = true;
        this.submitButton.setAttribute('disabled', 'disabled');
        this.submitButton.setAttribute('aria-disabled', 'true');
        const language = document.documentElement.lang.split('-')[0];
        const text = {
          ko: {
            failed: '장바구니 수량을 확인할 수 없습니다. 다시 추가하기 전에 장바구니를 새로고침해 주세요.',
            review: '장바구니가 변경되었을 수 있습니다. 다시 추가하기 전에 장바구니를 확인해 주세요.',
            link: '장바구니 확인',
          },
          zh: {
            failed: '未能确认购物车数量。再次加购前请刷新购物车。',
            review: '购物车可能已变化。再次加购前请先查看购物车。',
            link: '查看购物车',
          },
          en: {
            failed: 'Cart quantity could not be confirmed. Refresh the cart before adding again.',
            review: 'The cart may have changed. Check the cart before adding again.',
            link: 'View cart',
          },
        }[language] || {
          failed: 'Cart quantity could not be confirmed. Refresh the cart before adding again.',
          review: 'The cart may have changed. Check the cart before adding again.',
          link: 'View cart',
        };
        this.handleErrorMessage(`${message || window.cartStrings.error} ${reviewOnly ? text.review : text.failed}`);
        if (this.errorMessage) {
          const link = document.createElement('a');
          link.href = routes.cart_url;
          link.className = 'underlined-link';
          link.textContent = text.link;
          this.errorMessage.append(' ', link);
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (this.dataset.bsPurchaseBlocked === 'true') {
          this.variantIdInput.disabled = true;
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButtonText.textContent = this.dataset.bsPurchaseBlockedMessage;
          return;
        }
        if (this.beststarCartNeedsRefresh) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
          return;
        }
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

